import {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
  } from "react";
  import { useRouter } from "next/router";
  import BusinessContext from "./business-context";
  import UserContext from "./user-context";
  import { getDomains, createDomain, updateDomain, isDomainRegistered, generateDkim, getDomainDkim, domainRecordsVerification, createEmail, fetchDomainEmails } from "../lib/domains";
  import Loading from "../components/ui/loading";
  
  
  const DomainContext = createContext({
    domains: null,
    setDomains: () => { },
    fetchDomains: () => { },
    createNewDomain: () => { },
    updateExistingDomain: () => { },
    checkDomainRegistration: () => { },
    createDkim: () => { },
    getDkim: () => { },
    verifyAllDomainRecords: () => { },
    handleCreateEmail: () => { },
    renderEmails: () => { },
    emails: null,
    setEmails: () => { },
    checkIfUserHasEmail: () => { },
    userEmail: null,
    setUserEmail: () => { },
    activeDomain: null,
    setActiveDomain: () => { },
  });
  
  export const DomainProvider = (props) => {
    const [domains, setDomains] = useState(null);
    const [emails, setEmails] = useState(null);
    const { activeBusiness, staffList } = useContext(BusinessContext);
    const { user } = useContext(UserContext);
    const router = useRouter();
    const [userEmail, setUserEmail] = useState(null);
    const [activeDomain, setActiveDomain] = useState(null);
  
  
    
    //user has email checker
    const checkIfUserHasEmail = () => {
      if (emails && emails?.length === 0) {
        return false;
      }

      const currentStaff = staffList?.find(staff => staff?.user.email === user.email);
      
      if (emails && currentStaff) {
        const userEmailObj = emails?.find(email => email.owner === currentStaff?.id);
        setUserEmail(userEmailObj);
        return emails?.some(email => email.owner === currentStaff?.id);
      }
    }
        
    useEffect(() => {
      // Only proceed if all necessary data is loaded
      if ( staffList && user ) {
        if (router.pathname !== '/onboarding') {
          // Explicitly check if emails array exists and isn't empty
          if (emails && (emails?.length == 0 || !checkIfUserHasEmail())) {
            
            setTimeout(() => {
              router.push('/onboarding');
            }, 1000);
          }
        }
      }
    }, [staffList, router, emails, user]);

    
      
    const fetchDomains = (additionalCallback) => {
      const callback = (data) => {
        setActiveDomain(data.find(domain => domain.is_verified === true) || data[0] || null);
        additionalCallback && additionalCallback(data);
        setDomains(data);
      };
  
      const onError = (error) => {
        console.error("Error fetching domains:", error);
      };
      getDomains(callback, onError, activeBusiness, router);
    }
  
    const createNewDomain = (domain, additionalCallback) => {
  
      const payload = {
        fqdn: domain,
      }
      const callback = (data) => {
        setDomains((prevDomains) => [...prevDomains, data]);
        { additionalCallback && additionalCallback(data); }
      };
      const onError = (error) => {
        console.error("Error creating domain:", error);
      };
  
      createDomain(payload, callback, onError, activeBusiness, router);
  
    }
  
    const updateExistingDomain = (domainId, update, additionalCallback) => {
  
      const callback = (data, status) => {
  
        { additionalCallback && additionalCallback(data, status); }
      };
      const onError = (error) => {
        console.error("Error updating domain:", error);
      };
      updateDomain(domainId, update, callback, onError, activeBusiness, router);
    }
  
    const checkDomainRegistration = (domainId, additionalCallback, additonalError) => {
      const callback = (data) => {
        { additionalCallback && additionalCallback(data); }
      };
      const onError = (error) => {
        { additonalError && additonalError(error); }
        console.error("Error checking domain registration:", error);
      }
      isDomainRegistered(domainId, callback, onError, activeBusiness, router);
  
    }
  
    const createDkim = (domainId, additionalCallback) => {
      const callback = (data) => {
        { additionalCallback && additionalCallback(data); }
      };
      const onError = (error) => {
        console.error("Error creating DKIM:", error);
      };
      generateDkim(domainId, callback, onError, activeBusiness, router);
    }
  
    const getDkim = (domainId, additionalCallback, additionalError) => {
      const callback = (data) => {
        { additionalCallback && additionalCallback(data); }
      };
      const onError = (error) => {
        { additionalError && additionalError(error); }
        console.error("Error fetching DKIM:", error);
      };
      getDomainDkim(domainId, callback, onError, activeBusiness, router);
    }
  
    const verifyAllDomainRecords = (domainId, mxCallback, mxError, mxSubdomain, isMxVerified, spfCallback, spfError, spfSubdomain, isSpfVerified, dkimCallback, dkimError, dkimSubdomain, isDkimVerifed, dmarcCallback, dmarcError, dmarcSubdomain, isDmarcVerified) => {
      { isMxVerified === false && domainRecordsVerification(domainId, mxSubdomain, "mx", mxCallback, mxError, activeBusiness, router); }
      { isSpfVerified === false && domainRecordsVerification(domainId, spfSubdomain, "txt", spfCallback, spfError, activeBusiness, router); }
      { isDkimVerifed === false && domainRecordsVerification(domainId, dkimSubdomain, "txt", dkimCallback, dkimError, activeBusiness, router); }
      { isDmarcVerified === false && domainRecordsVerification(domainId, dmarcSubdomain, "txt", dmarcCallback, dmarcError, activeBusiness, router); }
    }
  
    const handleCreateEmail = (domainId, email, owner, createdBy, additionalCallback) => {
      const payload = {
        username: email,
        owner: owner,
        created_by: createdBy
      }
  
      const callBack = (json) => {
        { additionalCallback && additionalCallback(json); }
        renderEmails(domainId)
      }
  
      const onError = (error) => {
        console.error("Error creating email:", error);
      };
  
      createEmail(domainId, payload, callBack, onError, activeBusiness, router);
    }
  
    const renderEmails = (domainId, additionalCallback) => {
      const callBack = (json) => {
        { additionalCallback && additionalCallback(json); }
        setEmails(json);
      }
      const onError = (error) => {
        console.error("Error fetching emails:", error);
      };
      fetchDomainEmails(domainId, callBack, onError, activeBusiness, router);
    }
  
    useEffect(() => {
      if (!domains) {
        const callBack = (json) => {
          if (json.length > 0) {
            const verifiedDomain = json.find(domain => domain.is_verified === true);
            if (verifiedDomain) {
              renderEmails(verifiedDomain.id);
            } else {
              setEmails([]);
            }
          }
          else {
            setEmails([]);
          }
        }
        fetchDomains(callBack);
      }
    }, [domains, activeBusiness, router]);
  
    const context = {
      domains,
      setDomains,
      fetchDomains,
      createNewDomain,
      updateExistingDomain,
      checkDomainRegistration,
      createDkim,
      getDkim,
      verifyAllDomainRecords,
      handleCreateEmail,
      renderEmails,
      emails,
      setEmails,
      checkIfUserHasEmail,
      userEmail,
      setUserEmail,
      activeDomain,
      setActiveDomain,
    };
  
    return (
      <DomainContext.Provider value={context}>
        {props.children}
      </DomainContext.Provider>
    );
  };
  
  export default DomainContext;
  